import getStatePropsContext from './getStatePropsContext';
import {createInteractionId, mark, types as metrics} from '@servicenow/ui-metrics';
import {produce} from 'immer';
import {isValidOperation, isBatch, isUpdater} from './utils';
import * as operations from '../operators/operations';
import getHostVariables from '../getHostVariables';
import {schedule, types} from '../../scheduler';
import deferHelperExecution from './deferHelperExecution';
import {
	HELPERS_SCHEDULED,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	HELPERS_DEFER
} from '../../constants';
import log from '../../log';
import renderContextConsumers from '../../context/renderContextConsumers';
import {getContext, setContext} from '../../context/contexts';
const {IMMEDIATE, STATE_SET} = types;
const {
	declarativeOperations: {ASSIGN}
} = operations;

const resolveUpdate = (host, hostVariables, update, interactionId) => {
	if (!isUpdater(update)) return update;

	const statePropsContext = getStatePropsContext(host, interactionId);
	return update(statePropsContext);
};

function handleContextUpdate(host, hostVariables, update, interactionId) {
	const resolvedUpdate = resolveUpdate(host, hostVariables, update, interactionId);

	if (!resolvedUpdate) return {didUpdate: false, shouldRender: false};

	const {context: contextKey} = resolvedUpdate;
	const previousContext = getContext(contextKey);
	const {
		shouldUpdate = true,
		operation = previousContext !== null && typeof previousContext === 'object' ? ASSIGN : '',
		path = '',
		start,
		deleteCount,
		addItems,
		shouldRender = true,
		value
	} = resolvedUpdate;

	if (!shouldUpdate) return {didUpdate: false, shouldRender: false};

	const nextContext = produce(getContext(contextKey) || {}, draftState => {
		if (!operation) return value;

		if (!isValidOperation(operation)) {
			log(`Invalid update context operation: ${operation}`, {host, origin: 'handleContextUpdate'});
			return;
		}

		try {
			operations[operation](draftState, {
				path,
				value,
				start,
				deleteCount,
				addItems: Array.isArray(addItems) ? addItems : []
			});
		} catch (error) {
			log('Error while performing context update', {error, host, origin: 'handleContextUpdate'});
		}
	});

	if (nextContext === previousContext) return {didUpdate: false, shouldRender: false};

	setContext(contextKey, nextContext);
	return {didUpdate: true, shouldRender};
}

function handleContextUpdateBatch(host, hostVariables, batch, interactionId) {
	return batch.reduce(
		(acc, update) => {
			const {shouldRender, didUpdate} = handleContextUpdate(
				host,
				hostVariables,
				update,
				interactionId
			);

			const result = {...acc};
			if (didUpdate) result.didUpdate = true;

			if (shouldRender) result.shouldRender = true;

			return result;
		},
		{didUpdate: false, shouldRender: false}
	);
}

const resolveUpdateForMark = update => (update === 'function' ? 'FUNCTION' : update);
const transformUpdateForMark = (update, isBatch) => {
	if (isBatch) return update.map(resolveUpdateForMark);

	return resolveUpdateForMark(update);
};

function resolveInteractionId(interactionId, options) {
	if (interactionId) return interactionId;

	if (typeof options === 'string') return options;

	return createInteractionId();
}

export default function createUpdateContext(host, executionTiming) {
	const hostVariables = getHostVariables(host);

	return function updateContext(update, options = {}, interactionId) {
		const resolvedInteractionId = resolveInteractionId(interactionId, options);
		if (executionTiming === HELPERS_DEFER)
			mark(host, resolvedInteractionId, metrics.DEFER, {deferred: metrics.UPDATE_CONTEXT});
		const meta = {el: host, interactionId: resolvedInteractionId};
		deferHelperExecution(
			executionTiming,
			() => {
				// TODO: Remove this immediate option for a better scheduling alternative
				// https://code.devsnc.com/gist/dylan-mozlowski/07bf0ab13b2c8a12f0cc6e59bf4cf911
				const {
					[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]:
						optionallyImmediate = false
				} = options;

				schedule({
					fn() {
						const shouldBatch = isBatch(update);

						mark(host, resolvedInteractionId, metrics.UPDATE_CONTEXT, {
							update: transformUpdateForMark(update, shouldBatch)
						});

						const updateHandler = shouldBatch ? handleContextUpdateBatch : handleContextUpdate;
						const {didUpdate, shouldRender} = updateHandler(
							host,
							hostVariables,
							update,
							resolvedInteractionId
						);
						if (!didUpdate) return;

						if (shouldRender) {
							const contexts = shouldBatch ? update.map(({context}) => context) : [update.context];
							for (let i = 0; i < contexts.length; i++) {
								renderContextConsumers(contexts[i], resolvedInteractionId, {
									update: transformUpdateForMark(update, shouldBatch)
								});
							}
						}
					},
					meta,
					type: executionTiming !== HELPERS_SCHEDULED || optionallyImmediate ? IMMEDIATE : STATE_SET
				});
			},
			meta
		);
	};
}
